// My Performance — redesigned salesperson dashboard (Priority 5+6+7).
// One page, three tiers:
//   TOP    — My Performance card + Next Coaching Focus + My Actions
//   MIDDLE — Ten Criteria breakdown + Journey Stage + Language Patterns
//   BOTTOM — Active coaching records + link to Help hub
//
// Role-adaptive: uses `consultant_label` off the payload so tenants in
// non-sales verticals see "Team Member" / "Advisor" / "Support Rep"
// instead of "Consultant".
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, TrendingUp, TrendingDown, Target, MessageSquare, HelpCircle,
  ChevronRight, Award, AlertTriangle, BookOpen, ArrowUpRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../contexts/AuthContext';
import MyPlaybookAdherenceWidget from '../../components/MyPlaybookAdherenceWidget';
import { ChangePasswordButton } from '../../components/custom/layout/ChangePasswordButton';
import OnboardingVoiceTour, { ReplayTourButton } from '../../components/OnboardingVoiceTour';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const scoreTone = (s) => {
  if (s == null) return 'text-slate-400';
  if (s >= 80) return 'text-emerald-600';
  if (s >= 70) return 'text-teal-600';
  if (s >= 60) return 'text-amber-600';
  return 'text-rose-600';
};

const DeltaChip = ({ delta, suffix = 'pp' }) => {
  if (delta == null) return <span className="text-xs text-slate-400">— first window</span>;
  const positive = delta >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  const tone = positive ? 'text-emerald-600 bg-emerald-50' : 'text-rose-600 bg-rose-50';
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold px-1.5 py-0.5 rounded-full ${tone}`}>
      <Icon className="w-3 h-3" />
      {positive ? '+' : ''}{delta}{suffix}
    </span>
  );
};

const MyPerformancePage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [coachingRecords, setCoachingRecords] = useState([]);
  const [trainingProgram, setTrainingProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tourForceOpen, setTourForceOpen] = useState(0);   // increment to trigger replay

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/analytics/me/dashboard?days=30`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.data),
      axios.get(`${API}/coaching/records/mine`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.data?.records || []),
      // Fetch training_program so we can gate TFVF-only widgets. Failure
      // → treat as 'none' (widget hidden) rather than default-showing.
      axios.get(`${API}/training/mode`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.data?.program || 'none').catch(() => 'none'),
    ]).then(([d, recs, prog]) => {
      setData(d);
      setCoachingRecords(recs);
      setTrainingProgram(prog);
    }).catch(() => setData(null)).finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    );
  }
  if (!data || data.error) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <p className="text-slate-500">Unable to load your dashboard. Try refreshing.</p>
      </div>
    );
  }

  const mp = data.my_performance || {};
  const ncf = data.next_coaching_focus;
  const consultantLabel = data.consultant_label || 'Consultant';

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="my-performance-page">
      <OnboardingVoiceTour token={token} forceOpen={tourForceOpen} />
      <header className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
          My Performance
          <span className="text-sm font-normal text-slate-500 ml-3">last 30 days · {consultantLabel} view</span>
        </h1>
        <div className="flex items-center gap-2">
          <ChangePasswordButton variant="page" />
          <ReplayTourButton onReplay={() => setTourForceOpen((n) => n + 1)} />
        </div>
      </header>

      {/* ── TOP TIER ────────────────────────────────────────────── */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border-slate-200 shadow-sm" data-testid="my-performance-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-500 font-semibold uppercase tracking-wide">My Performance</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-baseline gap-3">
              <p className={`text-4xl font-bold tabular-nums ${scoreTone(mp.current_quality_score)}`}>
                {mp.current_quality_score ?? '—'}
              </p>
              <DeltaChip delta={mp.improvement_from_prior_period} />
            </div>
            <p className="text-xs text-slate-500">
              {mp.qualifying_call_count} qualifying call{mp.qualifying_call_count === 1 ? '' : 's'} ·
              site median <span className="font-semibold">{mp.site_median ?? '—'}</span>
            </p>
            {mp.insufficient_sample && (
              <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                <AlertTriangle className="w-3 h-3 text-amber-600" />
                <span className="text-xs text-amber-800">
                  Insufficient sample — {mp.qualifying_call_count} of {mp.ranking_min_calls} needed to rank
                </span>
              </div>
            )}
            {mp.best_recent_call && (
              <button
                onClick={() => navigate(`/calls/${mp.best_recent_call.call_id}`)}
                className="mt-2 text-xs text-teal-600 hover:text-teal-700 inline-flex items-center gap-1"
                data-testid="best-recent-call-link"
              >
                <Award className="w-3 h-3" />
                Best call: {mp.best_recent_call.score}
                <ChevronRight className="w-3 h-3" />
              </button>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm lg:col-span-2" data-testid="my-coaching-focus-card">
          <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm text-slate-500 font-semibold uppercase tracking-wide inline-flex items-center gap-1">
              <Target className="w-4 h-4 text-indigo-500" /> My Next Coaching Focus
            </CardTitle>
            <Link to="/help/coaching_selection" className="text-xs text-slate-400 hover:text-slate-600 inline-flex items-center gap-0.5">
              How this is chosen <HelpCircle className="w-3 h-3" />
            </Link>
          </CardHeader>
          <CardContent>
            {!ncf ? (
              <p className="text-sm text-slate-500">Not enough graded calls yet to pick a focus. Keep taking calls and check back.</p>
            ) : (
              <>
                <div className="flex items-baseline gap-3 mb-2">
                  <h3 className="text-lg font-bold text-slate-900">{ncf.criterion_label}</h3>
                  <span className={`text-xl font-bold tabular-nums ${scoreTone(ncf.current_score * 10)}`}>
                    {ncf.current_score?.toFixed(1)}
                  </span>
                  <DeltaChip delta={ncf.progress_indicator?.delta} suffix="" />
                </div>
                <p className="text-sm text-slate-600 mb-3">{ncf.why_it_matters}</p>
                {ncf.words_to_practise?.length > 0 && (
                  <div className="bg-emerald-50 border-l-4 border-emerald-400 rounded-r px-3 py-2 mb-2">
                    <p className="text-[11px] uppercase tracking-wider text-emerald-800 font-bold mb-1">Exact words to practise</p>
                    {ncf.words_to_practise.slice(0, 3).map((w, i) => (
                      <p key={i} className="text-sm text-emerald-900 italic">&quot;{w}&quot;</p>
                    ))}
                  </div>
                )}
                {ncf.evidence_calls?.length > 0 && (
                  <p className="text-xs text-slate-500">
                    Evidence: {ncf.evidence_calls.map((c) => (
                      <Link key={c.call_id} to={`/calls/${c.call_id}`} className="text-teal-600 hover:underline mr-2">
                        call {c.call_id.slice(-6)} ({c.score.toFixed(1)})
                      </Link>
                    ))}
                  </p>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </section>

      {/* ── Phase D — Personal Playbook Adherence (Q/V/O + per-archetype).
           TFVF V1.2 concept — only mount for tenants on the tfvf training
           program. Non-TF tenants (ipvsyabbit, future domains) would
           otherwise see the TFVF-flavoured widget chrome for an empty
           payload. Interim guard until Phase P2 reroutes this widget
           through effective_config(). */}
      {trainingProgram === 'tfvf' && (
        <section>
          <MyPlaybookAdherenceWidget />
        </section>
      )}

      {/* ── MIDDLE TIER: Ten Criteria ──────────────────────────────
           Interim guard: the labels served by /analytics/me/dashboard
           come from `core/my_dashboard.py::TEN_CRITERIA`, which is
           hard-coded to the TFVF framework (Green Brain / ALF / Rule
           of 3 / etc.). Non-TF tenants would see those labels applied
           to their calls even though their calls aren't graded on
           that framework. Hide the card entirely until Phase P2
           reroutes the payload through effective_config(). */}
      {trainingProgram === 'tfvf' && (
        <section>
          <Card className="border-slate-200 shadow-sm" data-testid="ten-criteria-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              <span>My Ten Criteria</span>
              {/* Only surface the help deep-link when the tenant has a
                  matching framework page. Non-TF tenants get a 404 on
                  /help/ten_criteria (see help_hub.py::_sections_for), so
                  hide the link entirely rather than dead-ending. */}
              {trainingProgram === 'tfvf' && (
                <Link to="/help/ten_criteria" className="text-xs font-normal text-slate-400 hover:text-slate-600 inline-flex items-center gap-1">
                  How my score is graded <HelpCircle className="w-3 h-3" />
                </Link>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.ten_criteria.map((c) => {
                const cur = c.current_score;
                const pct = cur != null ? Math.min(100, Math.max(0, (cur / 5) * 100)) : 0;
                return (
                  <div key={c.key} className="grid grid-cols-12 gap-3 items-center text-sm" data-testid={`criterion-${c.key}`}>
                    <div className="col-span-4 flex items-center gap-2">
                      <span className="font-semibold text-slate-800">{c.label}</span>
                      <span className="text-[10px] uppercase text-slate-400">w{c.weight}</span>
                    </div>
                    <div className="col-span-5 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${cur == null ? 'bg-slate-200' : cur >= 4 ? 'bg-emerald-500' : cur >= 3 ? 'bg-teal-500' : cur >= 2 ? 'bg-amber-500' : 'bg-rose-500'}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="col-span-2 flex items-center gap-2">
                      <span className={`font-bold tabular-nums ${scoreTone((cur || 0) * 20)}`}>{cur != null ? cur.toFixed(1) : '—'}</span>
                      <DeltaChip delta={c.delta} suffix="" />
                    </div>
                    <div className="col-span-1 text-right text-xs text-slate-400">n={c.calls_assessed}</div>
                  </div>
                );
              })}
            </div>
          </CardContent>
          </Card>
        </section>
      )}

      {/* Journey stage + Language patterns side-by-side */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-slate-200 shadow-sm" data-testid="journey-stage-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">My Journey-Stage Performance</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {data.journey_stage_performance.map((s) => (
              <div key={s.stage} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-0">
                <span className="text-slate-700 capitalize">{s.stage.replace('_', ' ')}</span>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-400">{s.calls} call{s.calls === 1 ? '' : 's'}</span>
                  <span className={`font-bold tabular-nums ${scoreTone(s.avg_score)}`}>
                    {s.avg_score ?? '—'}
                  </span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm" data-testid="language-patterns-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-slate-500" /> My Language Patterns
            </CardTitle>
            {data.language_patterns?.transcript_calls_analysed > 0 && (
              <p className="text-[11px] text-slate-500 mt-0.5">
                Based on {data.language_patterns.transcript_calls_analysed} transcribed calls in the last 30 days.
              </p>
            )}
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Interruption rate mini-stat */}
            {data.language_patterns?.transcript_calls_analysed > 0 && (
              <div
                className="flex items-center gap-3 text-xs bg-slate-50 border border-slate-200 rounded-lg p-2"
                data-testid="interruption-rate-stat"
              >
                <div className="w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center">
                  <span className={`text-xs font-bold ${
                    (data.language_patterns.interruption_rate_pct || 0) < 5 ? 'text-emerald-600'
                      : (data.language_patterns.interruption_rate_pct || 0) < 15 ? 'text-amber-600'
                        : 'text-rose-600'
                  }`}>
                    {data.language_patterns.interruption_rate_pct ?? 0}%
                  </span>
                </div>
                <div className="flex-1">
                  <p className="text-slate-800 font-medium">Interruption rate</p>
                  <p className="text-slate-500 text-[11px]">
                    {data.language_patterns.interruption_calls || 0} of {data.language_patterns.transcript_calls_analysed} calls
                    contained interruption cues ({data.language_patterns.interruption_total_cues || 0} total).
                    Aim to stay under 5%.
                  </p>
                </div>
              </div>
            )}
            {data.language_patterns?.filler_words?.length ? (
              <div className="space-y-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Filler words to drop</p>
                {data.language_patterns.filler_words.map((f) => (
                  <div key={f.word} className="flex items-center gap-3 text-sm">
                    <span className="font-mono bg-amber-50 text-amber-800 px-1.5 py-0.5 rounded text-xs">{f.word}</span>
                    <span className="text-slate-600 text-xs">×{f.uses} · in {f.pct_of_calls}% of calls</span>
                    <span className="ml-auto text-xs text-slate-500 italic">→ {f.suggested_replacement}</span>
                  </div>
                ))}
              </div>
            ) : data.language_patterns?.transcript_calls_analysed === 0 ? (
              <p className="text-sm text-slate-500 italic">No transcribed calls yet in this window — patterns unlock once Whisper transcribes your calls.</p>
            ) : (
              <p className="text-sm text-slate-500">No filler-word patterns detected. Keep it up.</p>
            )}
          </CardContent>
        </Card>
      </section>

      {/* ── BOTTOM TIER: Active coaching + Help ──────────────── */}
      {coachingRecords.length > 0 && (
        <section>
          <Card className="border-slate-200 shadow-sm" data-testid="active-coaching-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base inline-flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-indigo-500" /> Active Coaching Records
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {coachingRecords.map((r) => (
                <div key={r.id} className="border border-slate-200 rounded p-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-900">{r.focusBehaviour?.replace(/_/g, ' ')}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                      r.status === 'active' ? 'bg-indigo-100 text-indigo-800' :
                      r.status === 'improved' ? 'bg-emerald-100 text-emerald-800' :
                      r.status === 'not_improved' ? 'bg-rose-100 text-rose-800' :
                      'bg-slate-100 text-slate-700'
                    }`}>{r.status}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Assigned {new Date(r.assignedDate).toLocaleDateString()} · reviewing after {r.reassessmentCallCount} calls
                  </p>
                  {r.approvedLanguage?.length > 0 && (
                    <p className="text-xs text-emerald-700 mt-1 italic">&quot;{r.approvedLanguage[0]}&quot;</p>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </section>
      )}

      <div className="pt-2 pb-8">
        <Button
          onClick={() => navigate('/help')}
          variant="outline"
          className="text-sm"
          data-testid="open-help-hub"
        >
          <HelpCircle className="w-4 h-4 mr-2" />
          How my score is graded (full guide)
          <ArrowUpRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default MyPerformancePage;
